import React, { useEffect, useState } from 'react';
import apiService from '../../services/apiService';
import { useToast } from '../../contexts/ToastContext';

interface GCalSettings {
  calendarId: string;
  serviceAccountEmail: string | null;
  isReady: boolean;
  lastError: string | null;
  source: 'settings_file' | 'env' | 'default';
  envFallback: string | null;
}

const GoogleCalendarSettings: React.FC = () => {
  const { addToast } = useToast();
  const [settings, setSettings] = useState<GCalSettings | null>(null);
  const [calendarId, setCalendarId] = useState('');
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [testing, setTesting] = useState(false);
  const [resetting, setResetting] = useState(false);

  const load = async () => {
    setLoading(true);
    try {
      const resp = await apiService.get<GCalSettings>('/settings/google-calendar');
      if (resp.data?.success) {
        setSettings(resp.data.data);
        setCalendarId(resp.data.data.calendarId);
      } else {
        addToast(resp.data?.message || 'Konnte Google-Calendar-Einstellungen nicht laden.', 'error');
      }
    } catch (e: any) {
      addToast(e?.response?.data?.message || 'Konnte Google-Calendar-Einstellungen nicht laden.', 'error');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { load(); /* eslint-disable-line react-hooks/exhaustive-deps */ }, []);

  const handleSave = async () => {
    if (!calendarId.trim()) {
      addToast('Calendar-ID darf nicht leer sein.', 'error');
      return;
    }
    setSaving(true);
    try {
      const resp = await apiService.put<GCalSettings>('/settings/google-calendar', { calendarId: calendarId.trim() });
      if (resp.data?.success) {
        setSettings(resp.data.data);
        addToast('Calendar-ID gespeichert.', 'success');
      } else {
        addToast(resp.data?.message || 'Speichern fehlgeschlagen.', 'error');
      }
    } catch (e: any) {
      addToast(e?.response?.data?.message || 'Speichern fehlgeschlagen.', 'error');
    } finally {
      setSaving(false);
    }
  };

  const handleTest = async () => {
    setTesting(true);
    try {
      const resp = await apiService.post<any>('/settings/google-calendar/test', {});
      if (resp.data?.success) {
        const d = resp.data.data;
        addToast(`✅ Zugriff OK — "${d?.summary || 'Calendar'}" (${d?.timeZone || '?'})`, 'success');
      } else {
        addToast(resp.data?.message || 'Test fehlgeschlagen.', 'error');
      }
    } catch (e: any) {
      addToast(e?.response?.data?.message || 'Test fehlgeschlagen.', 'error');
    } finally {
      setTesting(false);
    }
  };

  const handleReset = async () => {
    if (!window.confirm('Persistierte Calendar-ID löschen und auf Default (env/Service-Account) zurückfallen?')) return;
    setResetting(true);
    try {
      const resp = await apiService.delete<GCalSettings>('/settings/google-calendar');
      if (resp.data?.success) {
        setSettings(resp.data.data);
        setCalendarId(resp.data.data.calendarId);
        addToast('Auf Default zurückgesetzt.', 'success');
      } else {
        addToast(resp.data?.message || 'Reset fehlgeschlagen.', 'error');
      }
    } catch (e: any) {
      addToast(e?.response?.data?.message || 'Reset fehlgeschlagen.', 'error');
    } finally {
      setResetting(false);
    }
  };

  if (loading) return <div className="bg-white rounded-lg shadow p-6 text-gray-500">Lade Google-Calendar-Einstellungen…</div>;
  if (!settings) return null;

  const sourceLabel = {
    settings_file: 'Persistiert (UI gespeichert)',
    env: 'Aus Umgebungsvariable',
    default: 'Default (Service-Account-Kalender)',
  }[settings.source];

  return (
    <section className="bg-white rounded-lg shadow p-6 space-y-6">
      <div>
        <h2 className="text-lg font-bold text-gray-900">📅 Google Calendar</h2>
        <p className="text-sm text-gray-600 mt-1">
          Steuert, in welchen Kalender Termine geschrieben werden. Der Service-Account selbst (Authentifizierung) bleibt file-based unter <code>server/config/service-account.json</code>.
        </p>
      </div>

      {/* Status row */}
      <div className="flex flex-wrap gap-2 items-center">
        <span className={`inline-flex items-center px-3 py-1 rounded-full text-xs font-semibold ${
          settings.isReady ? 'bg-green-100 text-green-800' : 'bg-red-100 text-red-700'
        }`}>
          {settings.isReady ? '● Verbunden' : '● Nicht verbunden'}
        </span>
        <span className="inline-flex items-center px-3 py-1 rounded-full text-xs font-medium bg-gray-100 text-gray-700">
          Quelle: {sourceLabel}
        </span>
        {settings.lastError && (
          <span className="inline-flex items-center px-3 py-1 rounded-full text-xs font-medium bg-red-50 text-red-700 border border-red-200">
            ⚠️ {settings.lastError}
          </span>
        )}
      </div>

      {/* Service account (read-only) */}
      <div>
        <label className="block text-xs font-bold text-gray-500 uppercase tracking-wider mb-1">Service-Account (read-only)</label>
        <input
          type="text"
          value={settings.serviceAccountEmail || '(unbekannt)'}
          readOnly
          className="w-full p-2 border border-gray-200 bg-gray-50 rounded text-sm text-gray-600 font-mono"
        />
        <p className="text-[11px] text-gray-500 mt-1">
          Damit ein Calendar editierbar ist, muss er für diese Adresse als <strong>"Make changes to events"</strong> freigegeben sein.
        </p>
      </div>

      {/* Calendar ID input */}
      <div>
        <label className="block text-xs font-bold text-gray-500 uppercase tracking-wider mb-1">Calendar-ID</label>
        <input
          type="text"
          value={calendarId}
          onChange={(e) => setCalendarId(e.target.value)}
          placeholder="xxxxxx@group.calendar.google.com"
          className="w-full p-2 border border-gray-300 rounded text-sm font-mono focus:ring-2 focus:ring-light-blue outline-none"
        />
        <p className="text-[11px] text-gray-500 mt-1">
          Findest du in Google Calendar → Einstellungen für den Kalender → "Kalender integrieren" → <em>Kalender-ID</em>.
        </p>
        {settings.envFallback && settings.source !== 'env' && (
          <p className="text-[11px] text-amber-700 mt-1">
            ℹ️ Env-Fallback verfügbar: <code className="bg-amber-50 px-1 rounded">{settings.envFallback}</code> (greift nach Reset auf Default).
          </p>
        )}
      </div>

      <div className="flex flex-wrap items-center justify-between gap-2 pt-2 border-t border-gray-100">
        <button
          onClick={handleReset}
          disabled={resetting || settings.source !== 'settings_file'}
          className="text-sm text-gray-600 hover:text-red-700 underline disabled:no-underline disabled:opacity-40 disabled:cursor-not-allowed"
          title={settings.source !== 'settings_file' ? 'Aktuell nicht persistiert — kein Reset nötig' : 'Auf env/Default zurückfallen'}
        >
          {resetting ? 'Setze zurück…' : 'Auf Default zurücksetzen'}
        </button>

        <div className="flex gap-2">
          <button
            onClick={handleTest}
            disabled={testing || !settings.isReady}
            className="px-4 py-2 bg-light-blue text-white rounded font-semibold disabled:opacity-50 hover:bg-opacity-90"
            title={!settings.isReady ? 'Service nicht initialisiert' : ''}
          >
            {testing ? 'Teste…' : 'Verbindung testen'}
          </button>
          <button
            onClick={handleSave}
            disabled={saving || calendarId.trim() === settings.calendarId}
            className="px-5 py-2 bg-navy-admin text-white rounded font-semibold disabled:opacity-50 hover:bg-opacity-90"
          >
            {saving ? 'Speichere…' : 'Speichern'}
          </button>
        </div>
      </div>
    </section>
  );
};

export default GoogleCalendarSettings;
